import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'

import { useCallback, useEffect, useState } from 'react'
import PrintReceiptPreviewModal from 'components/PrintReceiptPreview/PrintReceiptPreviewModal'
import usePromptpayPayment from 'components/QRPayment/usePromptpayPayment'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { MapPaymentIcon } from 'components/MapPaymentIcon/mapPaymentIcon'
import styles from '../paymentDialog.style'
import { logHWError } from 'utilities/logError'
import useSendPaymentToCFD from 'hooks/useSendMessageToCFD/useSendPaymentToCFD'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'

export function QRPromptPayDialog({
    openDialog,
    amount,
    orderNumber,
    onPaid,
    onClose,
}: {
    openDialog: boolean
    amount: number
    orderNumber: string
    onPaid: () => void
    onClose: () => void
}) {
    const {
        parseReceiptTemplateImage,
        printQRPromptpay,
        generateQRImage,
        getPromptPaySetting,
        setting,
    } = usePromptpayPayment()

    const [isPrinting, setIsPrinting] = useState<boolean>(false)
    const [qrImage, setQRImage] = useState<string>('')
    const [qrReceiptImage, setQRReceiptImage] = useState('')
    const [openPreview, setOpenPreview] = useState(false)
    const { enableOnScreenPrinter, enableAndroidPrinting } = useDefaultDevice()

    const onPrint = useCallback(async () => {
        if (enableOnScreenPrinter) {
            setQRReceiptImage(parseReceiptTemplateImage(amount, orderNumber))
            setOpenPreview(true)
        } else {
            setIsPrinting(true)
            await printQRPromptpay(amount, orderNumber, enableAndroidPrinting)
                .then(() => setIsPrinting(false))
                .catch((err) => logHWError('QRPromptPayDialog', 'print', err))
        }
    }, [
        amount,
        enableAndroidPrinting,
        enableOnScreenPrinter,
        orderNumber,
        parseReceiptTemplateImage,
        printQRPromptpay,
    ])
    useEffect(() => {
        setQRImage(generateQRImage(amount))
    }, [amount, generateQRImage])

    useEffect(() => {
        getPromptPaySetting()
    }, [getPromptPaySetting])

    const { sendQRToCFS } = useSendPaymentToCFD()

    useEffect(() => {
        if (qrImage && setting)
            sendQRToCFS(
                amount,
                qrImage,
                setting?.accountPromptPayName ?? '',
                setting.paymentTypeID!,
            )
    })
    return (
        <Dialog open={openDialog} onClose={onClose} fullWidth maxWidth="sm">
            <AppDialogTitle text="Prompt Pay" />
            <CloseDialogButton
                dialogName="QR preview dialog"
                onClick={onClose}
            />
            <DialogContent style={{ padding: 5 }}>
                <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                >
                    <Box pt={2}>
                        {qrImage ? (
                            <img
                                height={200}
                                src={qrImage}
                                alt="receipt-preview"
                            />
                        ) : (
                            <Skeleton height={200} />
                        )}
                    </Box>
                    <Typography color={'primary'}>
                        Amount: <MonetaryFormat isOnlyString value={amount} />
                    </Typography>
                    <Stack
                        p={2}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        width="100%"
                    >
                        <Button
                            sx={styles.confirmButton}
                            color="primary"
                            onClick={onPrint}
                            startIcon={
                                <MapPaymentIcon
                                    paymentTypeId={PAYMENT_TYPE_ID.PROMPTPAY}
                                    size={16}
                                    color="inherit"
                                />
                            }
                            disabled={isPrinting}
                        >
                            Print QR Code
                        </Button>
                        <Button
                            sx={{
                                ...styles.confirmButton,
                                width: 150,
                            }}
                            color="primary"
                            onClick={onPaid}
                        >
                            Paid
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
            <PrintReceiptPreviewModal
                title="Preview QR Prompt Pay"
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                previewMarkup={qrReceiptImage}
            />
        </Dialog>
    )
}
